import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { trackingClient } from 'clients';
import { HTTPError } from 'clients/HTTPClient';
import { getMockRecentCameras } from 'mocks/recentCameras';
import { AllCameraImage } from 'types/CarTracking';

const DEFAULT_PAGE = 1;
const MIN_DEMO_IMAGE_COUNT = 36;

type UseAllCameraImagesParams = {
    spotUuid: string;
    isOpen: boolean;
    pageSize: number;
    isDemoMode?: boolean;
};

type UseAllCameraImagesResult = {
    images: AllCameraImage[];
    hasMore: boolean;
    isInitialLoading: boolean;
    isLoadingMore: boolean;
    errorMessage: string;
    loadMore: () => void;
    reload: () => void;
};

function dedupeByUrlAndTimestamp(images: AllCameraImage[]): AllCameraImage[] {
    const seen = new Set<string>();
    return images.filter((image) => {
        const key = `${image.image_url}-${image.event_datetime}`;
        if (seen.has(key)) {
            return false;
        }
        seen.add(key);
        return true;
    });
}

function getDemoImages(spotUuid: string): AllCameraImage[] {
    const report = getMockRecentCameras(spotUuid, 'mixed');

    const demoImages: AllCameraImage[] = [
        ...report.overhead_images.map((image) => ({
            image_url: image.image_url,
            thumbnail_url: image.thumbnail_url || image.image_url,
            camera_type: 'Aerial',
            event_datetime: image.event_datetime,
        })),
        ...report.lpr_images.map((image) => ({
            image_url: image.image_url,
            thumbnail_url: image.thumbnail_url || image.image_url,
            camera_type: image.orientation,
            event_datetime: image.event_datetime,
        })),
    ];

    const sortedImages = demoImages.sort(
        (firstImage, secondImage) => secondImage.event_datetime - firstImage.event_datetime
    );
    if (sortedImages.length === 0 || sortedImages.length >= MIN_DEMO_IMAGE_COUNT) {
        return sortedImages;
    }

    const newestTimestamp = sortedImages[0].event_datetime;
    return Array.from({ length: MIN_DEMO_IMAGE_COUNT }, (_, index) => {
        const source = sortedImages[index % sortedImages.length];
        return {
            ...source,
            event_datetime: newestTimestamp - index,
        };
    });
}

export default function useAllCameraImages({
    spotUuid,
    isOpen,
    pageSize,
    isDemoMode = false,
}: UseAllCameraImagesParams): UseAllCameraImagesResult {
    const [images, setImages] = useState<AllCameraImage[]>([]);
    const [page, setPage] = useState(DEFAULT_PAGE);
    const [hasMore, setHasMore] = useState(false);
    const [isInitialLoading, setIsInitialLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [reloadCounter, setReloadCounter] = useState(0);
    const isCancelledRef = useRef(false);
    const isRequestInFlightRef = useRef(false);

    const demoImages = useMemo(() => (isDemoMode ? getDemoImages(spotUuid) : []), [isDemoMode, spotUuid]);

    const fetchPage = useCallback(
        async (pageToLoad: number, resetImages: boolean): Promise<void> => {
            if (!spotUuid || isRequestInFlightRef.current) {
                return;
            }

            isRequestInFlightRef.current = true;
            if (resetImages) {
                setIsInitialLoading(true);
            } else {
                setIsLoadingMore(true);
            }

            try {
                const newImages = isDemoMode
                    ? demoImages.slice((pageToLoad - 1) * pageSize, pageToLoad * pageSize)
                    : await trackingClient.getAllCameraImages(spotUuid, pageToLoad, pageSize);

                if (isCancelledRef.current) {
                    return;
                }

                setErrorMessage('');
                setPage(pageToLoad);
                setHasMore(newImages.length === pageSize);
                setImages((currentImages) =>
                    resetImages
                        ? dedupeByUrlAndTimestamp(newImages)
                        : dedupeByUrlAndTimestamp([...currentImages, ...newImages])
                );
            } catch (error) {
                if (isCancelledRef.current) {
                    return;
                }
                if (HTTPError.isHTTPError(error)) {
                    setErrorMessage(error.message);
                } else {
                    setErrorMessage(error instanceof Error ? error.message : 'Something went wrong');
                }
                if (resetImages) {
                    setImages([]);
                    setHasMore(false);
                }
            } finally {
                if (!isCancelledRef.current) {
                    setIsInitialLoading(false);
                    setIsLoadingMore(false);
                }
                isRequestInFlightRef.current = false;
            }
        },
        [demoImages, isDemoMode, pageSize, spotUuid]
    );

    useEffect(() => {
        isCancelledRef.current = false;

        if (!isOpen) {
            setImages([]);
            setPage(DEFAULT_PAGE);
            setHasMore(false);
            setErrorMessage('');
            setIsInitialLoading(false);
            setIsLoadingMore(false);
            return () => {
                isCancelledRef.current = true;
            };
        }

        void fetchPage(DEFAULT_PAGE, true);
        return () => {
            isCancelledRef.current = true;
        };
    }, [fetchPage, isOpen, reloadCounter]);

    const loadMore = useCallback(() => {
        if (!isOpen || !hasMore || isInitialLoading || isLoadingMore || !spotUuid) {
            return;
        }
        void fetchPage(page + 1, false);
    }, [fetchPage, hasMore, isInitialLoading, isLoadingMore, isOpen, page, spotUuid]);

    const reload = useCallback(() => {
        setReloadCounter((counter) => counter + 1);
    }, []);

    return {
        images,
        hasMore,
        isInitialLoading,
        isLoadingMore,
        errorMessage,
        loadMore,
        reload,
    };
}
